/**
 * The eight operations the public booking API offers, typed (BL-1513,
 * BL-1521, BL-1593).
 *
 * Each function returns a `BookingCall`: either the operation's union result
 * or a failure. A `__typename` outside the union the document was written
 * for is reported as an `unexpected` failure rather than passed through, so
 * a result type the backend adds later degrades to a generic error instead
 * of reaching a component that does not know how to render it.
 */
import {
  BookingResult,
  BookingResultType,
  CalendarFileResult,
  CancelBookingAsCandidateResult,
  ConfirmSlotResult,
  ConfirmSlotVariables,
  DeclineAllResult,
  HoldSlotResult,
  RescheduleBookingAsCandidateResult,
  RescheduleBookingVariables,
  SchedulingOfferResult,
} from './bookingApi.types';
import {
  BookingRefusalCode,
  BookingTransportResult,
  callBookingApi,
} from './bookingTransport';
import { BOOKING_OPERATION_IDS } from './operationIds';

export type BookingFailure =
  | { kind: 'refused'; code: BookingRefusalCode }
  | { kind: 'network' }
  | { kind: 'unexpected'; typename: string | undefined };

export type BookingCall<TResult> =
  | { ok: true; result: TResult }
  | { ok: false; failure: BookingFailure };

type Typed = { __typename: string };

const toCall = <TResult extends Typed>(
  transport: BookingTransportResult<Record<string, unknown>>,
  rootField: string,
  knownTypenames: readonly TResult['__typename'][]
): BookingCall<TResult> => {
  if (transport.kind === 'network') {
    return { ok: false, failure: { kind: 'network' } };
  }
  if (transport.kind === 'refused') {
    return { ok: false, failure: { kind: 'refused', code: transport.code } };
  }

  const payload = transport.data[rootField] as Partial<Typed> | undefined;
  const typename = payload?.__typename;
  if (typename === undefined || !knownTypenames.includes(typename)) {
    return { ok: false, failure: { kind: 'unexpected', typename } };
  }

  return { ok: true, result: payload as TResult };
};

export const fetchSchedulingOffer = async (
  token: string
): Promise<BookingCall<SchedulingOfferResult>> =>
  toCall(
    await callBookingApi(BOOKING_OPERATION_IDS.SchedulingOffer, token),
    'schedulingOffer',
    [
      BookingResultType.SCHEDULING_OFFER,
      BookingResultType.EXPIRED_LINK_STATE,
      BookingResultType.NO_SLOTS_AVAILABLE_STATE,
      BookingResultType.OFFER_CLOSED_STATE,
      BookingResultType.DECLINE_RECORDED,
      BookingResultType.LINK_REPLACED_STATE,
    ]
  );

export const fetchBooking = async (
  token: string
): Promise<BookingCall<BookingResult>> =>
  toCall(
    await callBookingApi(BOOKING_OPERATION_IDS.Booking, token),
    'booking',
    [
      BookingResultType.BOOKING_DETAILS,
      BookingResultType.NO_BOOKING_STATE,
      BookingResultType.LINK_REPLACED_STATE,
    ]
  );

export const holdSlot = async (
  token: string,
  seatId: string
): Promise<BookingCall<HoldSlotResult>> =>
  toCall(
    await callBookingApi(BOOKING_OPERATION_IDS.HoldSlot, token, { seatId }),
    'holdSlot',
    [
      BookingResultType.HOLD_ACCEPTED,
      BookingResultType.SEAT_UNAVAILABLE,
      BookingResultType.EXPIRED_LINK_STATE,
      BookingResultType.LINK_REPLACED_STATE,
    ]
  );

export const confirmSlot = async (
  token: string,
  variables: ConfirmSlotVariables
): Promise<BookingCall<ConfirmSlotResult>> =>
  toCall(
    await callBookingApi(BOOKING_OPERATION_IDS.ConfirmSlot, token, {
      ...variables,
    }),
    'confirmSlot',
    [
      BookingResultType.BOOKING_CONFIRMED,
      BookingResultType.HOLD_EXPIRED,
      BookingResultType.SEAT_UNAVAILABLE,
      BookingResultType.INVALID_TIMEZONE,
      BookingResultType.EXPIRED_LINK_STATE,
      BookingResultType.LINK_REPLACED_STATE,
    ]
  );

export const declineAll = async (
  token: string,
  reasonText?: string
): Promise<BookingCall<DeclineAllResult>> =>
  toCall(
    await callBookingApi(
      BOOKING_OPERATION_IDS.DeclineAll,
      token,
      reasonText === undefined ? {} : { reasonText }
    ),
    'declineAll',
    [
      BookingResultType.DECLINE_RECORDED,
      BookingResultType.DECLINE_REASON_TOO_LONG,
      BookingResultType.SEAT_UNAVAILABLE,
      BookingResultType.EXPIRED_LINK_STATE,
      BookingResultType.LINK_REPLACED_STATE,
    ]
  );

/** Reads the candidate's calendar file, which the API gives only when that file is their one calendar entry. */
export const fetchCalendarFile = async (
  token: string
): Promise<BookingCall<CalendarFileResult>> =>
  toCall(
    await callBookingApi(BOOKING_OPERATION_IDS.CalendarFile, token),
    'calendarFile',
    [
      BookingResultType.CALENDAR_FILE_CONTENT,
      BookingResultType.NO_BOOKING_STATE,
      BookingResultType.LINK_REPLACED_STATE,
    ]
  );

/** Cancels the candidate's confirmed interview. The same answer comes back if repeated. */
export const cancelBookingAsCandidate = async (
  token: string,
  bookingId: string
): Promise<BookingCall<CancelBookingAsCandidateResult>> =>
  toCall(
    await callBookingApi(BOOKING_OPERATION_IDS.CancelBookingAsCandidate, token, {
      bookingId,
    }),
    'cancelBookingAsCandidate',
    [
      BookingResultType.BOOKING_CANCELLED_BY_CANDIDATE,
      BookingResultType.CANDIDATE_ACTION_NOT_ALLOWED,
      BookingResultType.LINK_REPLACED_STATE,
    ]
  );

/**
 * Moves the candidate's confirmed interview to one of the other times the
 * booking read offered, or, with no seat named, asks the hiring team for
 * another time instead.
 */
export const rescheduleBookingAsCandidate = async (
  token: string,
  variables: RescheduleBookingVariables
): Promise<BookingCall<RescheduleBookingAsCandidateResult>> =>
  toCall(
    await callBookingApi(
      BOOKING_OPERATION_IDS.RescheduleBookingAsCandidate,
      token,
      { ...variables }
    ),
    'rescheduleBookingAsCandidate',
    [
      BookingResultType.BOOKING_CONFIRMED,
      BookingResultType.NO_OTHER_TIMES_STATE,
      BookingResultType.CANDIDATE_ACTION_NOT_ALLOWED,
      BookingResultType.SEAT_UNAVAILABLE,
      BookingResultType.INVALID_TIMEZONE,
      BookingResultType.LINK_REPLACED_STATE,
    ]
  );
